import { inject, Injectable } from '@angular/core';
import { LoginService } from './service/login.service';
import { concatMap, from, Observable, of } from 'rxjs';
import { RefreshSessionService } from './service/refresh-session.service';
import { CheckAuthService } from './service/check-auth.service';
import { OidcStorageService } from './storage/oidc-storage.service';
import { LoggerFactory } from '../shared/providers/logger/logger-factory.service';
import { LoggerWrapper } from '../shared/providers/logger/logger-wrapper.service';
import { openIdConfigurationNormal, openIdConfigurationSensible } from '../shared/conf/auth.conf';
import { AuthentificationType, LoginResponse, OidcSecurityService, OpenIdConfiguration } from './oidc-security.service';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AdfsOidcSecurityService implements OidcSecurityService {
  private logger: LoggerWrapper = inject(LoggerFactory).getInstance(this);

  constructor(
    private readonly loginService: LoginService,
    private readonly checkAuthService: CheckAuthService,
    private readonly refreshSessionService: RefreshSessionService,
    private readonly oidcStorageService: OidcStorageService
  ) {}

  redirectToAdfsLoginPage(type: AuthentificationType): void {
    this.logger.debug('[oidc] authorize', type);
    this.getOpenIDConfiguration(type).subscribe((config) => {
      this.loginService.redirectToAdfsLoginPage(config);
    });
  }

  checkAuth(type: AuthentificationType, url?: string): Observable<LoginResponse> {
    this.logger.debug('[oidc] checkAuth', type, url || 'no url');
    return this.getOpenIDConfiguration(type).pipe(
      concatMap((config) => {
        return this.checkAuthService.checkAuth(config, url);
      })
    );
  }

  forceRefreshSession(type: AuthentificationType): Observable<LoginResponse> {
    this.logger.debug('[oidc] forceRefreshSession', type);
    return this.getOpenIDConfiguration(type).pipe(
      concatMap((config) =>
        this.refreshSessionService
          .forceRefreshSession(config)
          .pipe(concatMap(() => this.checkAuthService.checkAuth(config)))
      )
    );
  }

  clear(type: AuthentificationType): Observable<void> {
    this.logger.debug('[oidc] clear', type);
    return from(this.oidcStorageService.clear(type));
  }

  getIdToken(type: AuthentificationType): Observable<string> {
    return from(this.oidcStorageService.getIdToken(type));
  }

  getRedirectUrl(type: AuthentificationType): Observable<string> {
    return this.getOpenIDConfiguration(type).pipe(
      map((conf) => {
        return conf.redirectUrl;
      })
    );
  }

  isOidcRequest(url: string): boolean {
    const allConfigurations = this.getAllConfigurations();
    const allCognitoUrls = allConfigurations.map((x) => [
      x.authWellknownEndpoints.authorizationEndpoint,
      x.authWellknownEndpoints.tokenEndpoint,
    ]);
    const allCognitoUrlsFlat = [].concat(...allCognitoUrls) as string[];
    for (const cognitoUrl of allCognitoUrlsFlat || []) {
      if (url.startsWith(cognitoUrl)) {
        return true;
      }
    }
    return false;
  }

  private getOpenIDConfiguration(type: AuthentificationType): Observable<OpenIdConfiguration> {
    return of(this.getAllConfigurations().find((conf) => conf.type === type));
  }

  private getAllConfigurations(): OpenIdConfiguration[] {
    return [openIdConfigurationNormal, openIdConfigurationSensible];
  }
}
